import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Loader2, UserPlus, UserX, Search, Mail, Phone } from 'lucide-react';
import { searchUsers } from '@/lib/firebase/queries/organizations/searchUsers';
import { getOrganizationMembers, getMemberDetails } from '@/lib/firebase/queries/organizations';
import { addMemberToOrganization, removeMemberFromOrganization } from '@/lib/firebase/queries/organizations/members';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import type { Organization } from '@/types/organization';
import type { User } from '@/types';

interface OrganizationMembersProps {
  organization: Organization | null;
  onSuccess?: () => void;
}

interface MemberWithDetails extends User {
  role: 'manager' | 'customer';
}

export function OrganizationMembers({ organization, onSuccess }: OrganizationMembersProps) {
  const [availableCustomers, setAvailableCustomers] = useState<User[]>([]);
  const [currentCustomers, setCurrentCustomers] = useState<MemberWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [availableSearchQuery, setAvailableSearchQuery] = useState('');
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const { currentUser } = useAuth();
  const { t, dir } = useLanguage();

  const loadCustomers = async () => {
    if (!organization) return;
    
    try {
      setLoading(true);
      console.log('🔍 Loading customers for organization:', organization.id);
      
      // Get all customers and organization members
      const [allCustomers, orgMembers] = await Promise.all([
        searchUsers('customer'),
        getOrganizationMembers(organization.id)
      ]);

      console.log('📊 All customers:', allCustomers.length);
      console.log('📊 Organization members:', orgMembers.length);

      // Get current customer IDs from organization members
      const currentCustomerIds = new Set(
        orgMembers
          .filter(member => member.role === 'customer')
          .map(member => member.userId)
      );

      // Split customers into current and available
      const currentCustomers = allCustomers
        .filter(customer => currentCustomerIds.has(customer.id))
        .map(customer => ({ ...customer, role: 'customer' as const })) as MemberWithDetails[];

      const availableCustomers = allCustomers
        .filter(customer => !currentCustomerIds.has(customer.id));

      console.log('📊 Current customers:', currentCustomers.length);
      console.log('📊 Available customers:', availableCustomers.length);

      setCurrentCustomers(currentCustomers);
      setAvailableCustomers(availableCustomers);
    } catch (error) {
      console.error('❌ Error loading customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, [organization]);

  // Debug search queries
  useEffect(() => {
    console.log('🔍 Current search query changed:', currentSearchQuery);
    console.log('🔍 Available search query changed:', availableSearchQuery);
  }, [currentSearchQuery, availableSearchQuery]);

  const filterCustomers = (customers: (User | MemberWithDetails)[], query: string) => {
    if (!query.trim()) return customers;
    
    const searchTerm = query.toLowerCase().trim();
    console.log('🔍 Searching for:', searchTerm, 'in', customers.length, 'customers');
    
    const filtered = customers.filter(customer => {
      // Handle null/undefined values safely
      const firstNameEn = customer.firstNameEn || '';
      const lastNameEn = customer.lastNameEn || '';
      const firstNameAr = customer.firstNameAr || '';
      const lastNameAr = customer.lastNameAr || '';
      const email = customer.email || '';
      const phone = customer.phone || '';
      
      const nameEn = `${firstNameEn} ${lastNameEn}`.toLowerCase().trim();
      const nameAr = `${firstNameAr} ${lastNameAr}`.toLowerCase().trim();
      const emailLower = email.toLowerCase().trim();
      const phoneLower = phone.toLowerCase().trim();
      
      const matches = nameEn.includes(searchTerm) || 
                     nameAr.includes(searchTerm) || 
                     emailLower.includes(searchTerm) || 
                     phoneLower.includes(searchTerm);
      
      if (matches) {
        console.log('✅ Match found:', customer.firstNameEn, customer.lastNameEn, 'for query:', searchTerm);
      } else {
        console.log('❌ No match for:', customer.firstNameEn, customer.lastNameEn, 'searching for:', searchTerm);
        console.log('   nameEn:', nameEn, 'nameAr:', nameAr, 'email:', emailLower, 'phone:', phoneLower);
      }
      
      return matches;
    });
    
    console.log('🔍 Search results:', filtered.length, 'matches');
    return filtered;
  };

  const filteredAvailableCustomers = filterCustomers(availableCustomers, availableSearchQuery) as User[];
  const filteredCurrentCustomers = filterCustomers(currentCustomers, currentSearchQuery) as MemberWithDetails[];
  
  // Debug filtered results
  console.log('🔍 Filtered current customers:', filteredCurrentCustomers.length, 'of', currentCustomers.length);
  console.log('🔍 Filtered available customers:', filteredAvailableCustomers.length, 'of', availableCustomers.length);
  
  // Debug search queries
  if (currentSearchQuery.trim()) {
    console.log('🔍 Current search query:', currentSearchQuery);
    console.log('🔍 Current customers data sample:', currentCustomers.slice(0, 2).map(c => ({
      id: c.id,
      firstNameEn: c.firstNameEn,
      lastNameEn: c.lastNameEn,
      email: c.email,
      phone: c.phone
    })));
  }

  const handleAddCustomer = async (customer: User) => {
    if (!organization || !currentUser) return;

    try {
      setProcessingId(customer.id);
      const success = await addMemberToOrganization(
        organization.id,
        customer.id,
        'customer',
        currentUser.uid
      );

      if (success) {
        // Note: Organization default habits system has been removed

        setAvailableCustomers(prev => prev.filter(c => c.id !== customer.id));
        setCurrentCustomers(prev => [...prev, { ...customer, role: 'customer' }]);
        onSuccess?.();
        toast.success(`${customer.firstNameEn} added as customer`);
      }
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error('Failed to add customer');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemoveCustomer = async (customer: MemberWithDetails) => {
    if (!organization) return;

    try {
      setProcessingId(customer.id);
      const success = await removeMemberFromOrganization(
        organization.id,
        customer.id
      );

      if (success) {
        setCurrentCustomers(prev => prev.filter(c => c.id !== customer.id));
        setAvailableCustomers(prev => [...prev, customer]);
        onSuccess?.();
        toast.success(`${customer.firstNameEn} removed from customers`);
      }
    } catch (error) {
      console.error('Error removing customer:', error);
      toast.error('Failed to remove customer');
    } finally {
      setProcessingId(null);
    }
  };

  if (!organization) return null;

  return (
    <div className="grid grid-cols-2 gap-6" dir={dir}>
      {/* Current Customers */}
      <div className="space-y-4">
        <h3 className="font-medium">{t('dashboard.admin.organizations.members.currentCustomers')}</h3>
        
        {/* Search Input */}
        <div className="relative">
          <Search className={`absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground ${dir === 'rtl' ? 'right-3' : 'left-3'}`} />
          <Input
            placeholder={dir === 'rtl' ? 'البحث في المستفيدين الحاليين...' : 'Search current customers...'}
            value={currentSearchQuery}
            onChange={(e) => {
              console.log('🔍 Current search input changed:', e.target.value);
              setCurrentSearchQuery(e.target.value);
            }}
            onKeyDown={(e) => e.key === 'Enter' && console.log('🔍 Enter pressed for current customers search')}
            className={dir === 'rtl' ? 'pr-10' : 'pl-10'}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
                  <ScrollArea className="h-[400px]">
          <div className="space-y-2 px-4 pb-4">
            {filteredCurrentCustomers.map((customer) => (
              <div
                key={customer.id}
                className="flex items-center gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-[166px]">{customer.email}</span>
                  </div>
                  {customer.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{customer.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {dir === 'rtl' 
                      ? `${customer.firstNameAr} ${customer.lastNameAr}`
                      : `${customer.firstNameEn} ${customer.lastNameEn}`
                    }
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveCustomer(customer)}
                  disabled={!!processingId}
                  className="flex-shrink-0 bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-700"
                >
                  {processingId === customer.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserX className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
              {currentCustomers.length === 0 && (
                <div className="text-center py-4 text-muted-foreground">
                                       {t('dashboard.admin.organizations.members.noCustomersAssigned')}
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Available Customers */}
      <div className="space-y-4">
        <h3 className="font-medium">{t('dashboard.admin.organizations.members.availableCustomers')}</h3>
        
        {/* Search Input */}
        <div className="relative">
          <Search className={`absolute top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground ${dir === 'rtl' ? 'right-3' : 'left-3'}`} />
          <Input
            placeholder={dir === 'rtl' ? 'البحث في المستفيدين المتاحين...' : 'Search available customers...'}
            value={availableSearchQuery}
            onChange={(e) => {
              console.log('🔍 Available search input changed:', e.target.value);
              setAvailableSearchQuery(e.target.value);
            }}
            onKeyDown={(e) => e.key === 'Enter' && console.log('🔍 Enter pressed for available customers search')}
            className={dir === 'rtl' ? 'pr-10' : 'pl-10'}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
                  <ScrollArea className="h-[400px]">
          <div className="space-y-2 px-4 pb-4">
            {filteredAvailableCustomers.map((customer) => (
              <div
                key={customer.id}
                className="flex items-center gap-3 p-3 rounded-lg border"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    <span className="truncate max-w-[166px]">{customer.email}</span>
                  </div>
                  {customer.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">{customer.phone}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {dir === 'rtl' 
                      ? `${customer.firstNameAr} ${customer.lastNameAr}`
                      : `${customer.firstNameEn} ${customer.lastNameEn}`
                    }
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleAddCustomer(customer)}
                  disabled={!!processingId}
                  className="flex-shrink-0 bg-green-50 hover:bg-green-100 text-green-600 hover:text-green-700"
                >
                  {processingId === customer.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
              {availableCustomers.length === 0 && (
                <div className="text-center py-4 text-muted-foreground">
                                       {t('dashboard.admin.organizations.members.noCustomersAvailable')}
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}